import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatEUR } from "@/lib/api";

const emptyAnag = { nome: "", cognome: "", email: "", telefono: "" };
const emptyFisc = { ragione_sociale: "", piva: "", codice_fiscale: "", sdi_pec: "", sede: "" };
const STATUSES = [["pending_approval", "In attesa"], ["demo", "Demo"], ["active", "Attivo"], ["suspended", "Sospeso"], ["expired", "Scaduto"]];
const ANAG = [["nome", "Nome"], ["cognome", "Cognome"], ["email", "Email"], ["telefono", "Telefono"]];
const FISC = [["ragione_sociale", "Ragione sociale"], ["piva", "P. IVA"], ["codice_fiscale", "Codice fiscale"], ["sdi_pec", "SDI / PEC"], ["sede", "Sede"]];

const Fields = ({ defs, value, onChange, prefix }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
    {defs.map(([k, l]) => (
      <div key={k}><Label className="text-xs">{l}</Label><Input value={value[k] || ""} onChange={(e) => onChange({ ...value, [k]: e.target.value })} data-testid={`${prefix}-${k}`} /></div>
    ))}
  </div>
);

export const CustomerDialog = ({ api, open, onOpenChange, customer, resellers, products, onSave, saving }) => {
  const isEdit = !!customer;
  const [f, setF] = useState(null);
  useEffect(() => {
    if (open) {
      setF(customer ? {
        reseller_id: customer.reseller_id, product_id: customer.product_id, module_ids: customer.module_ids || [],
        anagrafica: { ...emptyAnag, ...customer.anagrafica }, dati_fiscali: { ...emptyFisc, ...customer.dati_fiscali },
        status: customer.status, trial_end: (customer.trial_end || "").slice(0, 10), license_end: (customer.license_end || "").slice(0, 10),
      } : { reseller_id: "", product_id: "", module_ids: [], anagrafica: emptyAnag, dati_fiscali: emptyFisc });
    }
  }, [open, customer]);
  const { data: rDetail } = useQuery({
    queryKey: ["admin-reseller", f?.reseller_id],
    queryFn: async () => (await api.get(`/admin/resellers/${f.reseller_id}`)).data,
    enabled: !!f?.reseller_id && !isEdit,
  });
  if (!f) return null;
  const assigned = isEdit ? products : products.filter((p) => (rDetail?.products || []).some((a) => a.product_id === p.id));
  const product = products.find((p) => p.id === f.product_id);
  const total = product ? (product.price || 0) + (product.modules || []).filter((m) => f.module_ids.includes(m.id)).reduce((s, m) => s + (m.price || 0), 0) : 0;
  const toggleMod = (id, v) => setF({ ...f, module_ids: v ? [...f.module_ids, id] : f.module_ids.filter((x) => x !== id) });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="customer-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Modifica cliente" : "Nuovo cliente"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Rivenditore</Label>
              <Select value={f.reseller_id} onValueChange={(v) => setF({ ...f, reseller_id: v, ...(isEdit ? {} : { product_id: "", module_ids: [] }) })}>
                <SelectTrigger data-testid="cd-reseller"><SelectValue placeholder="Scelga il rivenditore" /></SelectTrigger>
                <SelectContent>{resellers.filter((r) => !r.blocked || r.id === customer?.reseller_id).map((r) => <SelectItem key={r.id} value={r.id}>{r.company?.ragione_sociale || r.email} ({r.codice_reseller})</SelectItem>)}</SelectContent>
              </Select>
              {isEdit && f.reseller_id !== customer.reseller_id && <p className="text-xs text-amber-700 mt-1">Le provvigioni già maturate restano al rivenditore precedente.</p>}
            </div>
            <div>
              <Label className="text-xs">Prodotto</Label>
              <Select value={f.product_id} disabled={isEdit || !f.reseller_id} onValueChange={(v) => setF({ ...f, product_id: v, module_ids: [] })}>
                <SelectTrigger data-testid="cd-product"><SelectValue placeholder={f.reseller_id ? "Scelga il prodotto" : "Prima il rivenditore"} /></SelectTrigger>
                <SelectContent>{assigned.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
              {!isEdit && f.reseller_id && rDetail && assigned.length === 0 && <p className="text-xs text-rose-700 mt-1">Nessun prodotto assegnato a questo rivenditore.</p>}
            </div>
          </div>
          {product && (
            <div className="border border-[#E2DDD5] rounded-lg p-3">
              <div className="text-xs font-semibold mb-2">Moduli</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                {(product.modules || []).map((m) => (
                  <label key={m.id} className="flex items-center gap-2 text-sm" data-testid={`cd-mod-${m.id}`}>
                    <Checkbox checked={f.module_ids.includes(m.id)} onCheckedChange={(v) => toggleMod(m.id, v)} />
                    <span className="flex-1">{m.name}</span>{m.price > 0 && <span className="text-xs font-mono text-slate-500">+{formatEUR(m.price)}</span>}
                  </label>
                ))}
              </div>
              <div className="text-right text-sm font-semibold mt-2" data-testid="cd-total">Totale annuo {formatEUR(total)}</div>
            </div>
          )}
          <div><div className="text-xs font-semibold mb-2">Dati di contatto</div><Fields defs={ANAG} value={f.anagrafica} onChange={(v) => setF({ ...f, anagrafica: v })} prefix="cd-anag" /></div>
          <div><div className="text-xs font-semibold mb-2">Dati fiscali</div><Fields defs={FISC} value={f.dati_fiscali} onChange={(v) => setF({ ...f, dati_fiscali: v })} prefix="cd-fisc" /></div>
          {isEdit && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Stato</Label>
                <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                  <SelectTrigger data-testid="cd-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Fine prova</Label><Input type="date" value={f.trial_end} onChange={(e) => setF({ ...f, trial_end: e.target.value })} data-testid="cd-trial-end" /></div>
              <div><Label className="text-xs">Scadenza licenza</Label><Input type="date" value={f.license_end} onChange={(e) => setF({ ...f, license_end: e.target.value })} data-testid="cd-license-end" /></div>
            </div>
          )}
          {!isEdit && <p className="text-xs text-slate-500">Al salvataggio viene generato il modulo d'ordine: lo faccia firmare, lo carichi e lo approvi per attivare il cliente.</p>}
        </div>
        <DialogFooter>
          <Button disabled={saving || !f.reseller_id || !f.product_id || !f.anagrafica.email} onClick={() => onSave(f)} className="bg-[#B84A2B] hover:bg-[#983C21] text-white" data-testid="cd-save">{isEdit ? "Salva modifiche" : "Crea cliente"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
